import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useStudio } from "./StudioContext";
import { resolveDept } from "./StudioShared";
import { StudioPage, Panel, SectionLabel, Drawer, DataTable, Loading, Badge, DBlock, DKV, cx } from "./StudioShared";
import {
  Plus, Pencil, Copy, Archive, ArchiveRestore, Send, CheckCircle2, Undo2,
  History, Flag, ListChecks, FolderCog, Trash2, X,
} from "lucide-react";

const STATUS_TONE = { draft: "slate", pending_review: "amber", published: "green", archived: "red" };
const STATUS_LABEL = { draft: "Draft", pending_review: "Pending Review", published: "Published", archived: "Archived" };
const PRIO = { high: "red", medium: "amber", low: "slate" };
const empty = { title: "", category: "Uncategorized", domain: "", industry: "cross-industry", tags: "",
  summary: "", best_practice: "", supporting_notes: "", related_strategists: [], related_departments: [],
  related_workflows: "", priority: "medium", confidence: 0.9, source: "ATF EKL" };

export default function KnowledgeManager() {
  const { strategists } = useStudio();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");
  const [manageId, setManageId] = useState(null);
  const [manage, setManage] = useState(null);
  const [versions, setVersions] = useState([]);
  const [audit, setAudit] = useState([]);
  const [editing, setEditing] = useState(null); // form object or null
  const [showCats, setShowCats] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (category) params.set("category", category);
    if (q) params.set("q", q);
    const { data } = await api.get(`/admin/mi/knowledge-manager?${params}`);
    setData(data);
  }, [status, category, q]);
  useEffect(() => { load(); }, [load]);

  const depts = useMemo(() => [...new Set((strategists || []).map((s) => resolveDept(s).name))], [strategists]);

  const openManage = async (id) => {
    setManageId(id); setManage(null); setVersions([]); setAudit([]);
    const [d, v, a] = await Promise.all([
      api.get(`/admin/mi/knowledge-manager/${id}`),
      api.get(`/admin/mi/knowledge-manager/${id}/versions`),
      api.get(`/admin/mi/knowledge-audit?entry_id=${id}`),
    ]);
    setManage(d.data); setVersions(v.data); setAudit(a.data);
  };
  const closeManage = () => { setManageId(null); setManage(null); };

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); toast.success(okMsg); await load(); if (manageId) await openManage(manageId); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };
  const transition = (id, target, msg) => act(() => api.post(`/admin/mi/knowledge-manager/${id}/transition`, { target }), msg);
  const flag = (id, f) => act(() => api.post(`/admin/mi/knowledge-manager/${id}/flag-review`, { flag: f }), f ? "Flagged for review" : "Flag cleared");
  const duplicate = (id) => act(() => api.post(`/admin/mi/knowledge-manager/${id}/duplicate`), "Duplicated as draft");
  const restoreVersion = (id, snapshot_id) => act(() => api.post(`/admin/mi/knowledge-manager/${id}/restore-version`, { snapshot_id }), "Restored to a new draft version");

  const startCreate = () => setEditing({ ...empty, _mode: "create" });
  const startEdit = (e) => setEditing({
    _mode: "edit", _id: e.id, ...empty, ...e,
    tags: (e.tags || []).join(", "), related_workflows: (e.related_workflows || []).join(", "),
    related_strategists: e.related_strategists || [], related_departments: e.related_departments || [],
  });
  const saveEdit = async () => {
    setBusy(true);
    const f = editing;
    const payload = {
      title: f.title, category: f.category, domain: f.domain, industry: f.industry,
      tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean),
      summary: f.summary, best_practice: f.best_practice, supporting_notes: f.supporting_notes,
      related_strategists: f.related_strategists, related_departments: f.related_departments,
      related_workflows: f.related_workflows.split(",").map((t) => t.trim()).filter(Boolean),
      priority: f.priority, confidence: parseFloat(f.confidence) || 0.9, source: f.source,
    };
    try {
      if (f._mode === "create") { await api.post("/admin/mi/knowledge-manager", payload); toast.success("Draft created"); }
      else { await api.put(`/admin/mi/knowledge-manager/${f._id}`, payload); toast.success("Saved"); }
      setEditing(null); await load(); if (manageId) await openManage(manageId);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  if (!data) return <StudioPage title="Knowledge Manager" question="Create, govern & publish knowledge."><Loading /></StudioPage>;
  const sc = data.status_counts || {};
  const cats = data.categories || [];
  const sel = "bg-navy-800 border border-white/10 text-white text-sm px-3 py-2 rounded-sm focus:border-gold focus:outline-none";

  const StatusPill = ({ e }) => (
    <span className="flex items-center gap-1.5">
      <Badge tone={STATUS_TONE[e.status]}>{STATUS_LABEL[e.status] || e.status}</Badge>
      {e.needs_review && <Flag size={13} className="text-amber-400" title="Needs review" />}
    </span>
  );

  return (
    <StudioPage title="Knowledge Manager" question="The publishing office — create, govern & publish knowledge." testid="knowledge-manager"
      actions={
        <div className="flex gap-2">
          <button onClick={() => setShowCats(true)} data-testid="km-manage-categories" className="flex items-center gap-2 text-sm text-slate-300 border border-white/10 hover:border-gold/40 px-3 py-2 rounded-sm"><FolderCog size={15} /> Categories</button>
          <button onClick={startCreate} data-testid="km-new-btn" className="flex items-center gap-2 text-sm text-navy bg-gold hover:bg-gold-light font-medium px-4 py-2 rounded-sm"><Plus size={16} /> New Knowledge</button>
        </div>
      }>
      {/* status tabs */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="km-status-tabs">
        <button onClick={() => setStatus("")} className={cx("text-xs px-3 py-1.5 rounded-sm border", !status ? "border-gold text-gold bg-gold/10" : "border-white/10 text-slate-400")}>All</button>
        {(data.statuses || []).map((s) => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`km-tab-${s}`}
            className={cx("text-xs px-3 py-1.5 rounded-sm border", status === s ? "border-gold text-gold bg-gold/10" : "border-white/10 text-slate-400")}>
            {STATUS_LABEL[s]} ({sc[s] || 0})
          </button>
        ))}
        <select value={category} onChange={(e) => setCategory(e.target.value)} className={cx(sel, "ml-auto")} data-testid="km-category-filter">
          <option value="">All categories</option>
          {cats.map((c) => <option key={c.id} value={c.name}>{c.name} ({c.count})</option>)}
        </select>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className={cx(sel, "w-48")} data-testid="km-search" />
      </div>

      <DataTable testid="km-table" rows={data.entries} onRowClick={(e) => openManage(e.id)} rowTestid={(e) => `km-row-${e.id}`}
        empty="No knowledge entries. Create one to get started."
        columns={[
          { key: "title", label: "Title", render: (e) => <span className="text-white">{e.title}</span> },
          { key: "category", label: "Category", render: (e) => <span className="text-slate-400">{e.category}</span> },
          { key: "domain", label: "Domain", render: (e) => <span className="text-slate-500 text-xs">{e.domain}</span> },
          { key: "ver", label: "Version", className: "font-mono text-xs", render: (e) => e.current_version || e.version },
          { key: "status", label: "Status", render: (e) => <StatusPill e={e} /> },
          { key: "updated", label: "Updated", className: "text-slate-500 text-xs", render: (e) => e.updated ? new Date(e.updated).toLocaleDateString() : "—" },
        ]} />

      {/* Manage drawer */}
      <Drawer open={!!manageId} onClose={closeManage} testid="km-manage-drawer"
        title={manage?.title || "Knowledge"} subtitle={manage ? `${manage.category} · ${manage.domain} · v${manage.current_version || manage.version}` : ""}>
        {!manage ? <Loading /> : (
          <>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge tone={STATUS_TONE[manage.status]}>{STATUS_LABEL[manage.status]}</Badge>
              {manage.needs_review && <Badge tone="amber">Needs review</Badge>}
              <Badge tone={PRIO[manage.priority]}>{manage.priority}</Badge>
              <Badge tone="slate">confidence {Math.round((manage.confidence || 0) * 100)}%</Badge>
            </div>

            {/* lifecycle actions */}
            <div className="flex flex-wrap gap-2" data-testid="km-actions">
              {manage.status === "draft" && <>
                <ActBtn onClick={() => transition(manage.id, "pending_review", "Submitted for review")} icon={Send} testid="km-submit">Submit for Review</ActBtn>
                <ActBtn onClick={() => startEdit(manage)} icon={Pencil} testid="km-edit">Edit</ActBtn>
              </>}
              {manage.status === "pending_review" && <>
                <ActBtn onClick={() => transition(manage.id, "published", "Approved & published")} icon={CheckCircle2} primary testid="km-approve">Approve &amp; Publish</ActBtn>
                <ActBtn onClick={() => transition(manage.id, "draft", "Returned to draft")} icon={Undo2} testid="km-return-draft">Return to Draft</ActBtn>
                <ActBtn onClick={() => startEdit(manage)} icon={Pencil} testid="km-edit">Edit</ActBtn>
              </>}
              {manage.status === "published" && <>
                <ActBtn onClick={() => startEdit(manage)} icon={Pencil} testid="km-edit">Edit (new version)</ActBtn>
                <ActBtn onClick={() => flag(manage.id, !manage.needs_review)} icon={Flag} testid="km-flag">{manage.needs_review ? "Clear Flag" : "Flag for Review"}</ActBtn>
              </>}
              {manage.status === "archived" && <>
                <ActBtn onClick={() => transition(manage.id, "draft", "Restored to draft")} icon={ArchiveRestore} testid="km-restore">Restore</ActBtn>
              </>}
              <ActBtn onClick={() => duplicate(manage.id)} icon={Copy} testid="km-duplicate">Duplicate</ActBtn>
              {manage.status !== "archived" && <ActBtn onClick={() => transition(manage.id, "archived", "Archived")} icon={Archive} testid="km-archive">Archive</ActBtn>}
            </div>

            {manage.summary && <DBlock title="Summary"><p className="text-slate-300 text-sm leading-relaxed">{manage.summary}</p></DBlock>}
            {manage.best_practice && <DBlock title="Best Practice"><p className="text-slate-300 text-sm leading-relaxed">{manage.best_practice}</p></DBlock>}
            {manage.supporting_notes && <DBlock title="Supporting Notes"><p className="text-slate-400 text-sm leading-relaxed">{manage.supporting_notes}</p></DBlock>}
            {(manage.tags || []).length > 0 && <div className="flex flex-wrap gap-1.5">{manage.tags.map((t) => <span key={t} className="text-[10px] text-slate-400 bg-white/5 px-2 py-0.5 rounded-sm">{t}</span>)}</div>}
            <div className="space-y-1.5 border-t border-white/5 pt-3">
              <DKV label="Related strategists" value={(manage.related_strategists || []).join(", ")} />
              <DKV label="Related departments" value={(manage.related_departments || []).join(", ")} />
              <DKV label="Related workflows" value={(manage.related_workflows || []).join(", ")} />
            </div>

            {/* audit trail metadata */}
            <div className="space-y-1.5 border-t border-white/5 pt-3">
              <SectionLabel className="text-gold mb-1">Audit Trail</SectionLabel>
              <DKV label="Author" value={manage.author} />
              <DKV label="Editor" value={manage.editor} />
              <DKV label="Reviewer" value={manage.reviewer} />
              <DKV label="Submitted" value={manage.submitted_at ? new Date(manage.submitted_at).toLocaleString() : null} />
              <DKV label="Approved" value={manage.approved_at ? new Date(manage.approved_at).toLocaleString() : null} />
              <DKV label="Published" value={manage.published_at ? new Date(manage.published_at).toLocaleString() : null} />
              <DKV label="Archived" value={manage.archived_at ? new Date(manage.archived_at).toLocaleString() : null} />
            </div>

            {/* version history */}
            <DBlock title={`Version History (${versions.length})`}>
              {versions.length === 0 ? <p className="text-slate-500 text-sm">No snapshots yet.</p> : (
                <ul className="space-y-2" data-testid="km-versions">
                  {versions.map((v) => (
                    <li key={v.id} className="flex items-center justify-between bg-navy border border-white/10 rounded-sm px-3 py-2" data-testid={`km-version-${v.version_seq}`}>
                      <div className="min-w-0">
                        <div className="text-sm text-white font-mono">v{v.version} <span className="text-slate-500">· {STATUS_LABEL[v.status] || v.status}</span></div>
                        <div className="text-xs text-slate-500 truncate">{v.change_summary} · {v.editor} · {v.snapshot_at ? new Date(v.snapshot_at).toLocaleDateString() : ""}</div>
                      </div>
                      <button onClick={() => restoreVersion(manage.id, v.id)} disabled={busy} data-testid={`km-restore-version-${v.version_seq}`}
                        className="text-xs text-gold hover:underline flex items-center gap-1 shrink-0 ml-3"><History size={13} /> Restore</button>
                    </li>
                  ))}
                </ul>
              )}
            </DBlock>

            {audit.length > 0 && (
              <DBlock title="Recent Activity">
                <ul className="space-y-1.5" data-testid="km-audit">
                  {audit.slice(0, 8).map((a) => (
                    <li key={a.id} className="text-xs text-slate-400 flex gap-2">
                      <span className="text-gold shrink-0">·</span>
                      <span>{a.action.replace(/knowledge_|_/g, " ").trim()} — {a.actor_name} · {a.timestamp ? new Date(a.timestamp).toLocaleString() : ""}</span>
                    </li>
                  ))}
                </ul>
              </DBlock>
            )}
          </>
        )}
      </Drawer>

      {/* Editor drawer */}
      <Drawer open={!!editing} onClose={() => setEditing(null)} testid="km-editor-drawer"
        title={editing?._mode === "create" ? "New Knowledge Entry" : "Edit Knowledge"}
        subtitle={editing?._mode === "edit" && editing.status === "published" ? "Editing a published entry creates a new draft version" : "Saved as a draft"}>
        {editing && (
          <div className="space-y-4" data-testid="km-editor-form">
            <Field label="Title"><input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className={cx(sel, "w-full")} data-testid="km-field-title" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Category"><select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className={cx(sel, "w-full")} data-testid="km-field-category">
                {cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                {!cats.some((c) => c.name === editing.category) && <option value={editing.category}>{editing.category}</option>}
              </select></Field>
              <Field label="Domain"><input value={editing.domain} onChange={(e) => setEditing({ ...editing, domain: e.target.value })} className={cx(sel, "w-full")} placeholder="e.g. seo" data-testid="km-field-domain" /></Field>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Industry"><input value={editing.industry} onChange={(e) => setEditing({ ...editing, industry: e.target.value })} className={cx(sel, "w-full")} /></Field>
              <Field label="Priority"><select value={editing.priority} onChange={(e) => setEditing({ ...editing, priority: e.target.value })} className={cx(sel, "w-full")}><option value="high">high</option><option value="medium">medium</option><option value="low">low</option></select></Field>
              <Field label="Confidence"><input type="number" step="0.05" min="0" max="1" value={editing.confidence} onChange={(e) => setEditing({ ...editing, confidence: e.target.value })} className={cx(sel, "w-full")} /></Field>
            </div>
            <Field label="Tags (comma separated)"><input value={editing.tags} onChange={(e) => setEditing({ ...editing, tags: e.target.value })} className={cx(sel, "w-full")} data-testid="km-field-tags" /></Field>
            <Field label="Summary"><textarea value={editing.summary} onChange={(e) => setEditing({ ...editing, summary: e.target.value })} rows={2} className={cx(sel, "w-full")} data-testid="km-field-summary" /></Field>
            <Field label="Best Practice"><textarea value={editing.best_practice} onChange={(e) => setEditing({ ...editing, best_practice: e.target.value })} rows={3} className={cx(sel, "w-full")} data-testid="km-field-best-practice" /></Field>
            <Field label="Supporting Notes"><textarea value={editing.supporting_notes} onChange={(e) => setEditing({ ...editing, supporting_notes: e.target.value })} rows={2} className={cx(sel, "w-full")} /></Field>
            <Field label="Related Strategists"><select multiple value={editing.related_strategists} onChange={(e) => setEditing({ ...editing, related_strategists: [...e.target.selectedOptions].map((o) => o.value) })} className={cx(sel, "w-full h-28")} data-testid="km-field-strategists">
              {(strategists || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select></Field>
            <Field label="Related Departments"><select multiple value={editing.related_departments} onChange={(e) => setEditing({ ...editing, related_departments: [...e.target.selectedOptions].map((o) => o.value) })} className={cx(sel, "w-full h-20")}>
              {depts.map((d) => <option key={d} value={d}>{d}</option>)}
            </select></Field>
            <Field label="Related Workflows (comma separated)"><input value={editing.related_workflows} onChange={(e) => setEditing({ ...editing, related_workflows: e.target.value })} className={cx(sel, "w-full")} /></Field>
            <div className="flex gap-2 pt-2 border-t border-white/10">
              <button onClick={saveEdit} disabled={busy || !editing.title || !editing.domain} data-testid="km-save-btn" className="flex-1 bg-gold hover:bg-gold-light text-navy font-medium text-sm py-2.5 rounded-sm disabled:opacity-50">{busy ? "Saving…" : "Save"}</button>
              <button onClick={() => setEditing(null)} className="px-4 text-sm text-slate-400 hover:text-white">Cancel</button>
            </div>
          </div>
        )}
      </Drawer>

      {showCats && <CategoryManager cats={cats} onClose={() => setShowCats(false)} onChange={load} />}
    </StudioPage>
  );
}

const Field = ({ label, children }) => (
  <div><label className="text-xs text-slate-500 block mb-1">{label}</label>{children}</div>
);

function ActBtn({ onClick, icon: Icon, children, primary, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={cx("flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-sm border transition-colors",
        primary ? "bg-gold text-navy border-gold hover:bg-gold-light font-medium" : "border-white/15 text-slate-300 hover:border-gold/40 hover:text-white")}>
      <Icon size={13} /> {children}
    </button>
  );
}

function CategoryManager({ cats, onClose, onChange }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try { await api.post("/admin/mi/knowledge-categories", { name }); toast.success("Category added"); setName(""); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const del = async (id) => {
    try { await api.delete(`/admin/mi/knowledge-categories/${id}`); toast.success("Category removed"); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <Panel className="w-full max-w-md p-6" data-testid="km-category-manager" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg text-white">Manage Categories</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X size={18} /></button>
        </div>
        <div className="flex gap-2 mb-4">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="New category name" data-testid="km-category-name"
            className="flex-1 bg-navy border border-white/10 text-white text-sm px-3 py-2 rounded-sm focus:border-gold focus:outline-none" />
          <button onClick={add} disabled={busy} data-testid="km-category-add" className="bg-gold text-navy px-4 rounded-sm text-sm font-medium disabled:opacity-50">Add</button>
        </div>
        <div className="space-y-1.5 max-h-72 overflow-y-auto">
          {cats.map((c) => (
            <div key={c.id} className="flex items-center justify-between bg-navy border border-white/10 rounded-sm px-3 py-2">
              <span className="text-sm text-slate-300">{c.name} <span className="text-slate-600 text-xs">· {c.count}</span></span>
              <button onClick={() => del(c.id)} disabled={c.count > 0} title={c.count > 0 ? "In use — cannot delete" : "Delete"}
                className="text-slate-500 hover:text-red-400 disabled:opacity-30" data-testid={`km-category-del-${c.id}`}><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-600 mt-3">Only the Owner (Knowledge Administrator) can manage the taxonomy.</p>
      </Panel>
    </div>
  );
}
